import * as Constants from "../core/Constants";

/**
 * Type representing a contact with digital signing method
 */
export interface DigitalSigningContact {
  contactKey: string;
  signingMethod: string;
}

/**
 * Checks if a question requires digital signing contact handling
 */
export function isDigitalSigningQuestion(questionCode: string): boolean {
  return (
    questionCode === Constants.GeneralInformationQuestion.Q0018 ||
    questionCode === Constants.GeneralInformationQuestion.Q0034
  );
}

/**
 * Normalizes user value to handle both legacy format and digital signing format
 */
export function normalizeDigitalSigningValue(
  selectedOption: string,
  existingValue: any
): DigitalSigningContact | string {
  if (
    existingValue &&
    typeof existingValue === "object" &&
    "signingMethod" in existingValue
  ) {
    return {
      contactKey: selectedOption,
      signingMethod: (existingValue as any).signingMethod,
    };
  }

  return {
    contactKey: selectedOption,
    signingMethod:
      selectedOption !== Constants.HelpfulConstants.PleaseSelect
        ? Constants.SigningMethod.BankIDSE
        : Constants.HelpfulConstants.PleaseSelect,
  };
}

/**
 * Extracts the contact key from a value that might be a string or DigitalSigningContact object
 */
export function extractContactKey(value: any): string {
  if (value && typeof value === "object" && "contactKey" in value) {
    return (value as DigitalSigningContact).contactKey;
  }
  return value as string;
}

/**
 * Extracts the signing method from a value, defaulting to BankIDSE
 */
export function extractSigningMethod(value: any): string {
  if (value && typeof value === "object" && "signingMethod" in value) {
    return (value as DigitalSigningContact).signingMethod;
  }
  return Constants.SigningMethod.BankIDSE;
}

/**
 * Validates that all digital signing contact values are properly filled
 */
export function validateDigitalSigningContacts(userValue: any[]): boolean {
  if (!Array.isArray(userValue) || userValue.length === 0) {
    return false;
  }

  return userValue.every((value) => {
    if (!value) return false;

    if (
      typeof value === "object" &&
      "contactKey" in value &&
      "signingMethod" in value
    ) {
      const contactKey = (value as DigitalSigningContact).contactKey;
      const signingMethod = (value as DigitalSigningContact).signingMethod;
      return (
        contactKey &&
        contactKey !== Constants.HelpfulConstants.PleaseSelect &&
        signingMethod &&
        signingMethod !== Constants.HelpfulConstants.PleaseSelect
      );
    }

    return false;
  });
}

/**
 * Updates the signing method for a specific contact
 */
export function updateSigningMethod(
  userValueArray: any[],
  contactIndex: number,
  selectedMethod: string
): any[] {
  return userValueArray.map((value, index) => {
    if (index === contactIndex) {
      const contactKey = extractContactKey(value);
      return {
        contactKey: contactKey,
        signingMethod: selectedMethod,
      } as DigitalSigningContact;
    }

    // Ensure all values are in the proper format
    if (value && typeof value === "object" && "contactKey" in value) {
      return value;
    }

    return {
      contactKey: value,
      signingMethod: Constants.SigningMethod.BankIDSE,
    } as DigitalSigningContact;
  });
}

/**
 * Gets available signing method options for the dropdown
 */
export function getSigningMethodOptions(t: (key: string) => string) {
  return [
    {
      key: Constants.SigningMethod.BankIDSE,
      value: t("Options.SigningMethod.bankid-se"),
    },
    {
      key: Constants.SigningMethod.BankIDNO,
      value: t("Options.SigningMethod.bankid-no"),
    },
    {
      key: Constants.SigningMethod.EIDFI,
      value: t("Options.SigningMethod.eID-fi"),
    },
    {
      key: Constants.SigningMethod.EIDDK,
      value: t("Options.SigningMethod.eID-dk"),
    },
    {
      key: Constants.SigningMethod.TouchSign,
      value: t("Options.SigningMethod.touch-sign"),
    },
  ];
}

/**
 * Gets the display value for a signing method
 */
export function getSigningMethodDisplayValue(
  method: string,
  t: (key: string) => string
): string {
  switch (method) {
    case Constants.SigningMethod.BankIDSE:
      return t("Options.SigningMethod.bankid-se");
    case Constants.SigningMethod.BankIDNO:
      return t("Options.SigningMethod.bankid-no");
    case Constants.SigningMethod.EIDFI:
      return t("Options.SigningMethod.eID-fi");
    case Constants.SigningMethod.EIDDK:
      return t("Options.SigningMethod.eID-dk");
    case Constants.SigningMethod.TouchSign:
      return t("Options.SigningMethod.touch-sign");
    default:
      return t("Options.SigningMethod.bankid-se");
  }
}
